import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import { CnpjInput, DateInput, TextInput } from '@core/components/Inputs';
import ReadOnlyInput from '@core/components/Inputs/ReadOnlyInput';
import { SelectInput, SelectSimple } from '@core/components/Select';
import { formatCNPJ, formatCPF } from '@format';
import { Divider, Grid, Tooltip } from '@material-ui/core';
import { Logomarca } from 'app/assets/logomarca';
import SelectCnae from 'app/components/Select/SelectCnae';
import { excluirAlert } from 'app/services/alerts';
import empresaApi from 'app/services/api/empresaApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { useFormikContext } from 'formik';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { FaCloudUploadAlt, FaCut, FaTrash } from 'react-icons/fa';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useDispatch } from 'react-redux';
import { EmpresaModais } from '../../EmpresaModais';
import { Comercializacao, IndicadorInsricaoEstadual } from '../../enums';

const getExtension = (arquivo) => {
  const { name } = arquivo;
  const lastDot = name.lastIndexOf('.');
  const ext = name.substring(lastDot + 1).toLowerCase();
  return ext;
};

const getCroppedImg = async (imgRef, crop, onBlob = () => {}) => {
  if (!imgRef.current) {
    return;
  }

  const image = imgRef.current;
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = 400;
  canvas.height = 400;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, 400, 400);

  canvas.toBlob(
    (blob) => {
      onBlob(blob);
    },
    'image/jpeg',
    1
  );

  return canvas.toDataURL();
};

const MostrarCrop = ({ modal, imgCrop, setUpImg, closeModal, imgOriginal, setLoading, handleApiError }) => {
  const [crop, setCrop] = useState({ aspect: 1, height: 100, width: 100, unit: 'px', x: 0, y: 0 });
  const imgRef = useRef(null);
  const [, setCompletedCrop] = useState(null);
  const formikContext = useFormikContext();
  const [empresa] = useContext(GlobalContext).empresa;

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  return (
    <>
      <SimpleDialog open={modal === EmpresaModais.recortarLogo} onClose={null}>
        <SimpleDialogContent
          header={{
            title: 'Recortar Logomarca',
            icon: <FaCut />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={() => closeModal('')}>
                  Fechar
                </CancelButton>
                <SaveButton
                  color="buttonSave"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const onBlob = async (blob) => {
                        try {
                          const formData = new FormData();
                          formData.append('arquivo', blob, `logomarca.${getExtension(imgOriginal)}`);
                          if (formikContext.values.id) {
                            formData.append('empresaId', formikContext.values.id);
                          }

                          const resp = await empresaApi.uploadLogoMarca(formData);
                          empresa.logomarcaId = resp.data.id;
                          formikContext.setFieldValue('logomarcaId', resp.data.id);
                          setLoading(false);
                        } catch (err) {
                          setLoading(false);
                          handleApiError(err);
                        }
                      };

                      const cropped = await getCroppedImg(imgRef, crop, onBlob);
                      setUpImg(cropped);
                      empresa.logomarca = cropped;

                      closeModal('');
                    } catch (err) {
                      setLoading(false);
                      handleApiError(err);
                    }
                  }}
                >
                  Recortar
                </SaveButton>
              </>
            ),
          }}
          content={
            <>
              <div style={{ textAlign: 'center', marginTop: 10 }}>
                <ReactCrop
                  src={imgCrop}
                  onImageLoaded={onLoad}
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                />
              </div>
            </>
          }
        />
      </SimpleDialog>
    </>
  );
};

const DadosGerais = ({
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  submitCount,
  setLoading,
  handleApiError,
}) => {
  const inputRef = useRef(null);
  const [modal, setModal] = useState('');
  const [, setUpImg] = useState();
  const [imgCrop, setImgCrop] = useState(null);
  const [esconderIcones, setEsconderIcones] = useState(true);
  const dispatch = useDispatch();
  const [empresa] = useContext(GlobalContext).empresa;
  const [imgOriginal, setImgOriginal] = useState(null);

  const _errors = {
    nome: (submitCount > 0 || touched?.pessoa?.nome) && errors?.pessoa?.nome,
    tipoPessoa: (submitCount > 0 || touched?.pessoa?.tipoPessoa) && errors?.pessoa?.tipoPessoa,
    razaoSocial:
      (submitCount > 0 || touched?.pessoa?.pessoaJuridica?.razaoSocial) && errors?.pessoa?.pessoaJuridica?.razaoSocial,
    inscricaoMunicipal: (submitCount > 0 || touched?.pessoa?.inscricaoMunicipal) && errors?.pessoa?.inscricaoMunicipal,
    inscricaoEstadual:
      (submitCount > 0 || touched?.pessoa?.pessoaJuridica?.inscricaoEstadual) &&
      errors?.pessoa?.pessoaJuridica?.inscricaoEstadual,
    emailPrincipal: (submitCount > 0 || touched?.emailPrincipal) && errors?.emailPrincipal,
    nomeRemetentePrincipal: (submitCount > 0 || touched?.nomeRemetentePrincipal) && errors?.nomeRemetentePrincipal,
    emailCobranca: (submitCount > 0 || touched?.emailCobranca) && errors?.emailCobranca,
    nomeRemetenteCobranca: (submitCount > 0 || touched?.nomeRemetenteCobranca) && errors?.nomeRemetenteCobranca,
    cnpj: (submitCount > 0 || touched?.pessoa?.pessoaJuridica?.cnpj) && errors?.pessoa?.pessoaJuridica?.cnpj,
    comercializacao: (submitCount > 0 || touched?.comercializacao) && errors?.comercializacao,
    cpf: (submitCount > 0 || touched?.pessoa?.pessoaFisica?.cpf) && errors?.pessoa?.pessoaFisica?.cpf,
    identidade:
      (submitCount > 0 || touched?.pessoa?.pessoaFisica?.identidade) && errors?.pessoa?.pessoaFisica?.identidade,
    dataNascimento:
      (submitCount > 0 || touched?.pessoa?.pessoaFisica?.dataNascimento) &&
      errors?.pessoa?.pessoaFisica?.dataNascimento,
    identificadorEstrangeiro:
      (submitCount > 0 || touched?.pessoa?.pessoaFisica?.identificadorEstrangeiro) &&
      errors?.pessoa?.pessoaFisica?.identificadorEstrangeiro,
  };

  const onSelectFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 200000 || e.target.files.length >= 2) {
      showMessage('É permitido anexar apenas 1 imagem de no máximo 200KB', 'error');
      return;
    }

    const { name } = file;
    const lastDot = name.lastIndexOf('.');
    const ext = name.substring(lastDot + 1).toLowerCase();

    if (ext !== 'jpg' && ext !== 'jpeg' && ext !== 'png') {
      showMessage('É permitido anexar somente imagem nos formatos: JPEG, JPG e PNG', 'error');
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => setImgCrop(reader.result));
    reader.readAsDataURL(file);
    setModal('recortarLogo');

    setImgOriginal(file);
  };

  const handleUploadArquivo = () => {
    excluirAlert({
      confirmar: async () => {
        setLoading(true);
        await empresaApi.excluirLogoMarca(empresa.logomarcaId);
        empresa.logomarca = undefined;
        empresa.logomarcaId = undefined;
        setLoading(false);
      },
      opcoes: {
        title: 'Você realmente deseja excluir?',
        text: '',
      },
    });
  };

  const onMouseEnter = () => {
    setEsconderIcones(false);
  };

  const onMouseLeave = () => {
    setEsconderIcones(true);
  };

  const showMessage = (message, variant) => {
    dispatch(
      Actions.showMessage({
        message,
        variant,
      })
    );
  };

  const renderCampoInscricaoMunicipal = () => {
    return (
      <Grid item xs={4}>
        <TextInput
          id="pessoa.inscricaoMunicipal"
          label="Inscrição municipal"
          value={values?.pessoa?.inscricaoMunicipal}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={!!_errors.inscricaoMunicipal}
          error={_errors.inscricaoMunicipal}
          maxLength={20}
        />
      </Grid>
    );
  };

  return (
    <>
      <MostrarCrop
        modal={modal}
        imgCrop={imgCrop}
        setUpImg={setUpImg}
        closeModal={setModal}
        imgOriginal={imgOriginal}
        setLoading={setLoading}
        handleApiError={handleApiError}
      />
      <div hidden>
        <input type="file" accept=".png, .jpg, .jpeg" onChange={onSelectFile} ref={inputRef} value="" />
      </div>
      <Grid item xs={2}>
        <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} style={{ position: 'relative' }}>
          <Tooltip title="Informe a logo da empresa" placement="top">
            <div>
              {empresa.logomarca ? (
                <img src={empresa.logomarca} alt="" />
              ) : (
                <img src={Logomarca} alt="" style={{ width: 200 }} />
              )}

              <Tooltip title="Excluir imagem" placement="bottom" hidden={esconderIcones || !empresa.logomarca}>
                <div style={{ position: 'absolute', top: 0, right: 0 }}>
                  <FaTrash size={20} style={{ opacity: 0.5, cursor: 'pointer' }} onClick={handleUploadArquivo} />
                </div>
              </Tooltip>

              <div hidden={esconderIcones} style={{ position: 'absolute', top: '35%', left: '35%' }}>
                <FaCloudUploadAlt
                  size={40}
                  style={{ opacity: 0.5, cursor: 'pointer' }}
                  onClick={() => {
                    inputRef.current.click();
                  }}
                />
              </div>
            </div>
          </Tooltip>
        </div>
      </Grid>
      <Grid item xs={10}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextInput
              id="pessoa.nome"
              label="Nome"
              value={values?.pessoa?.nome}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={!!_errors.nome}
              error={_errors.nome}
              maxLength={100}
              required
            />
          </Grid>

          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
            <SelectSimple
              defaultValue="JURIDICA"
              label="Tipo de pessoa"
              id="pessoa.tipoPessoa"
              onChange={handleChange}
              value={values?.pessoa?.tipoPessoa}
              options={[
                { title: 'Pessoa Jurídica', id: 'JURIDICA' },
                { title: 'Pessoa Física', id: 'FISICA' },
              ]}
            />
          </Grid>

          {values?.pessoa?.tipoPessoa === 'JURIDICA' && (
            <>
              <CnpjInput
                id="pessoa.pessoaJuridica.cnpj"
                name="pessoa.pessoaJuridica.cnpj"
                label="CNPJ"
                value={formatCNPJ(values?.pessoa?.pessoaJuridica?.cnpj)}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={!!_errors.cnpj}
                error={_errors.cnpj}
              />

              <Grid item xs={12}>
                <TextInput
                  id="pessoa.pessoaJuridica.razaoSocial"
                  label="Razão social"
                  value={values?.pessoa?.pessoaJuridica?.razaoSocial}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={!!_errors.razaoSocial}
                  error={_errors.razaoSocial}
                  maxLength={150}
                />
              </Grid>
            </>
          )}

          {values?.pessoa?.tipoPessoa === 'FISICA' && (
            <Grid item xs={6}>
              <TextInput
                id="pessoa.pessoaFisica.cpf"
                label="CPF"
                value={formatCPF(values?.pessoa?.pessoaFisica?.cpf)}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={14}
                touched={!!_errors.cpf}
                error={_errors.cpf}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={values?.pessoa?.tipoPessoa === 'JURIDICA' ? 9 : 8}>
        <Tooltip title="Informe o CNAE principal da empresa" placement="top">
          <div>
            <SelectCnae
              id="cnae"
              label="Principal atividade econômica"
              value={values?.cnae}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
        </Tooltip>
      </Grid>
      {values?.pessoa?.tipoPessoa === 'JURIDICA' && (
        <>
          <Grid item xs={3}>
            <DateInput
              id="pessoa.pessoaJuridica.dataFundacao"
              label="Data da fundação"
              value={values?.pessoa?.pessoaJuridica?.dataFundacao}
              onChange={handleChange}
              onBlur={handleBlur}
              disableFuturesetShowModal
            />
          </Grid>
          <Grid item xs={4}>
            <SelectInput
              id="pessoa.pessoaJuridica.indicadorInscricaoEstadual"
              label="Indicador de inscrição estadual"
              value={values?.pessoa?.pessoaJuridica?.indicadorInscricaoEstadual}
              onChange={handleChange}
              onBlur={handleBlur}
              options={[
                { id: 'NAO_CONTRIBUINTE', title: IndicadorInsricaoEstadual.NAO_CONTRIBUINTE },
                { id: 'CONTRIBUINTE', title: IndicadorInsricaoEstadual.CONTRIBUINTE },
                { id: 'CONTRIBUINTE_ISENTO', title: IndicadorInsricaoEstadual.CONTRIBUINTE_ISENTO },
              ]}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              id="pessoa.pessoaJuridica.inscricaoEstadual"
              label="Inscrição estadual"
              value={values?.pessoa?.pessoaJuridica?.inscricaoEstadual}
              onChange={handleChange}
              onBlur={handleBlur}
              replace={{ searchValue: /[^0-9]/gm, newValue: '' }}
              pattern={/^[0-9]*$/gm}
              touched={!!_errors.inscricaoEstadual}
              error={_errors.inscricaoEstadual}
              maxLength={20}
            />
          </Grid>
          {renderCampoInscricaoMunicipal()}
        </>
      )}
      {values?.pessoa?.tipoPessoa === 'FISICA' && (
        <>
          <Grid item xs={4}>
            <TextInput
              id="pessoa.pessoaFisica.identidade"
              name="pessoa.pessoaFisica.identidade"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values?.pessoa?.pessoaFisica?.identidade}
              fullWidth
              maxLength={10}
              variant="outlined"
              label="Identidade"
              touched={!!_errors.identidade}
              error={_errors.identidade}
            />
          </Grid>
          <Grid item xs={4}>
            <DateInput
              fullWidth
              disableFuture
              id="pessoa.pessoaFisica.dataNascimento"
              name="pessoa.pessoaFisica.dataNascimento"
              label="Data de nascimento"
              value={values?.pessoa?.pessoaFisica?.dataNascimento}
              onChange={handleChange}
              touched={!!_errors.dataNascimento}
              error={_errors.dataNascimento}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <TextInput
              id="pessoa.pessoaFisica.identificadorEstrangeiro"
              name="pessoa.pessoaFisica.identificadorEstrangeiro"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values?.pessoa?.pessoaFisica?.identificadorEstrangeiro}
              fullWidth
              variant="outlined"
              label="Identificador estrangeiro"
              maxLength={15}
              touched={!!_errors.identificadorEstrangeiro}
              error={_errors.identificadorEstrangeiro}
            />
          </Grid>
          {renderCampoInscricaoMunicipal()}
        </>
      )}
      <Grid item xs={8}>
        <SelectInput
          id="comercializacao"
          label="Comercialização"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.comercializacao}
          options={[
            { id: 'SERVICO', title: Comercializacao.SERVICO },
            { id: 'PRODUTO', title: Comercializacao.PRODUTO },
            { id: 'AMBOS', title: Comercializacao.AMBOS },
          ]}
          required
          touched={!!_errors.comercializacao}
          error={_errors.comercializacao}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyInput id="planoComercial" label="Meu plano contratado" value={values?.planoComercial?.nome} />
      </Grid>
      <Grid item xs={12}>
        <Divider></Divider>
      </Grid>
      <Grid item xs={8}>
        <TextInput
          id="emailPrincipal"
          label="E-mail principal"
          value={values.emailPrincipal}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={!!_errors.emailPrincipal}
          error={_errors.emailPrincipal}
          maxLength={150}
          required
        />
      </Grid>
      <Grid item xs={4}>
        <TextInput
          id="nomeRemetentePrincipal"
          label="Nome do remetente do e-mail"
          value={values.nomeRemetentePrincipal}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={!!_errors.nomeRemetentePrincipal}
          error={_errors.nomeRemetentePrincipal}
          maxLength={150}
          required
        />
      </Grid>
      <Grid item xs={8}>
        <TextInput
          id="emailCobranca"
          label="E-mail cobrança"
          value={values.emailCobranca}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={!!_errors.emailCobranca}
          error={_errors.emailCobranca}
          maxLength={150}
          required
        />
      </Grid>
      <Grid item xs={4}>
        <TextInput
          id="nomeRemetenteCobranca"
          label="Nome do remetente do e-mail"
          value={values.nomeRemetenteCobranca}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={!!_errors.nomeRemetenteCobranca}
          error={_errors.nomeRemetenteCobranca}
          maxLength={150}
          required
        />
      </Grid>
    </>
  );
};

const TabDadosGerais = ({ setLoading, handleApiError }) => {
  const formikContext = useFormikContext();
  const { values, handleChange, handleBlur, errors, touched, submitCount } = formikContext;

  return (
    <DadosGerais
      values={values}
      handleChange={handleChange}
      handleBlur={handleBlur}
      errors={errors}
      touched={touched}
      submitCount={submitCount}
      setLoading={setLoading}
      handleApiError={handleApiError}
    />
  );
};

export default TabDadosGerais;
